import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FlowBuilderContext } from '../context';
import { flushUiChangesHack } from 'pages/AutomationEngine/Engine/utilities';
import Handle from '__pages/AutomationEngine/Engine/Handle';
import { Button as ButtonKDS } from '@kizen/kds/Button';
import {
  ADD_BUTTON_HEIGHT,
  AddSmartConnectorButton,
  addTransformButtonOptionsTranslated,
  addUploadButtonOptionsTranslated,
} from 'pages/SmartConnectors/SmartConnector/SmartConnectorBuilder/FlowBuilder/Steps/AddTransformButtom';
import type { FlowStep } from '../types';
import { useStore } from 'reactflow';
import { ConfiguredStepCard } from './ConfiguredStepCard';
import ErrorStepCard from '../Steps/ErrorStepCard';
import ErrorBoundaryResettable from 'components/Kizen/ErrorBoundaryResettable';
import { JunctionNode } from 'pages/AutomationEngine/Engine/Flow/nodes';
import { isStepSlotNode } from 'pages/AutomationEngine/Engine/Flow/layout';
import type { FlowNodeType } from 'pages/AutomationEngine/Engine/Flow/types';
import { StepSlot } from 'pages/AutomationEngine/Engine/Flow/styles';
import {
  defaultConnectionGetRequestsStep,
  defaultConnectionStep,
} from '../steps';

const transformTypes = ['action', 'condition'];

const isPlacingTransformStepAddNode = (
  placing: boolean,
  lastPlacingStep: FlowStep | null
) =>
  placing && lastPlacingStep && transformTypes.includes(lastPlacingStep?.type);

const isPlacingUploadStepAddNode = (
  placing: boolean,
  lastPlacingStep: FlowStep | null
) =>
  placing && lastPlacingStep && !transformTypes.includes(lastPlacingStep?.type);

type NodeProps = {
  id: string;
  data: any;
};

export const UploadNode = ({ id, data, ...props }: NodeProps) => {
  const { t } = useTranslation();

  const { placing, interacting, lastPlacingStep, dropStep } =
    useContext(FlowBuilderContext);

  const placingUploadStep = isPlacingUploadStepAddNode(
    placing,
    lastPlacingStep
  );

  const isPlacingAllowed =
    !placing ||
    !placingUploadStep ||
    (data.index !== lastPlacingStep?.order &&
      data.index - 1 !== lastPlacingStep?.order);

  const handleOnDrop = async () => {
    if (isPlacingAllowed) {
      // TODO this is abnoxious, but it is necessary so that
      // during layout after the drop that react-flow doesn't
      // measure the visible "Place Here" button before it
      // disappears. Essentially gives time for the button
      // to go back to its initial state then get measured.
      await flushUiChangesHack();
      dropStep({
        step: lastPlacingStep,
        ...data,
      });
    }
  };

  const handleSelectOption = ({ value }: { value: string }) => {
    dropStep({
      step: { type: value, order: data.index },
      ...data,
    });
  };

  return isPlacingAllowed ? (
    <AddSmartConnectorButton
      centered={false}
      placing={placingUploadStep}
      disabled={isPlacingTransformStepAddNode(placing, lastPlacingStep)}
      onDrop={handleOnDrop}
      showMenu={interacting ? false : undefined}
      options={addUploadButtonOptionsTranslated(t)}
      onSelectOption={handleSelectOption}
      dataQa="add-upload-step"
      iconName="cloud-upload"
      title={t('Add Load Step')}
      {...props}
      {...data}
    />
  ) : (
    <JunctionNode height={ADD_BUTTON_HEIGHT} />
  );
};

export const AdditionalVariableNode = () => {
  const { t } = useTranslation();
  const { setConnections, preRelease, dropStep } =
    useContext(FlowBuilderContext);
  return (
    <div>
      <ButtonKDS
        color="secondary"
        onClick={() => {
          setConnections(
            preRelease
              ? [defaultConnectionStep, defaultConnectionGetRequestsStep]
              : [defaultConnectionStep]
          );
          dropStep({
            step: defaultConnectionStep,
            branch: false,
            leaf: false,
            label: '',
            id: '',
            parentKey: null,
          });
        }}
        rightIcon="action-show-variables"
        variant="standard"
      >
        {t('Additional Variables')}
      </ButtonKDS>
      <Handle
        id="handle-left"
        type="target"
        position="left"
        style={{ left: 0, opacity: 0 }}
      />
      <Handle
        id="handle-right"
        type="source"
        position="right"
        style={{ right: 0 }}
      />
    </div>
  );
};

export const TransformAddNode = ({ id, data, ...props }: NodeProps) => {
  const { t } = useTranslation();

  const { preRelease, placing, interacting, lastPlacingStep, dropStep } =
    useContext(FlowBuilderContext);

  return (
    <AddSmartConnectorButton
      centered={!data.leaf || data.branch}
      placing={isPlacingTransformStepAddNode(placing, lastPlacingStep)}
      disabled={
        !preRelease || isPlacingUploadStepAddNode(placing, lastPlacingStep)
      }
      onDrop={async () => {
        // TODO this is abnoxious, but it is necessary so that
        // during layout after the drop that react-flow doesn't
        // measure the visible "Place Here" button before it
        // disappears. Essentially gives time for the button
        // to go back to its initial state then get measured.
        await flushUiChangesHack();
        dropStep({
          step: lastPlacingStep,
          ...data,
        });
      }}
      showMenu={interacting ? false : undefined}
      options={preRelease ? addTransformButtonOptionsTranslated(t) : []}
      onSelectOption={({ value }: { value: string }) => {
        dropStep({
          step: { type: value },
          ...data,
        });
      }}
      dataQa="add-transform-step"
      iconName="plus"
      {...props}
      {...data}
    />
  );
};

type StepNodeProps = {
  type: FlowNodeType;
  data: any;
  disableMenu: boolean;
};

export const StepNode = ({
  type,
  data,
  disableMenu = false,
  ...props
}: StepNodeProps) => {
  const { placing, interacting, showStats, placeStep } =
    useContext(FlowBuilderContext);

  const zoom = useStore((state) => state.transform[2]);

  const handleOnStart = useCallback(() => {
    placeStep(data);
  }, [placeStep, data]);

  const handleOnStop = useCallback(() => placeStep(false), [placeStep]);

  const handleOnSelectMenu = useCallback(({ value }: { value: string }) => {
    if (value === 'delete') {
    }
  }, []);

  const ErrorCard = () => (
    <ErrorStepCard
      {...props}
      type={type}
      step={data}
      showMovingPlaceholder={false}
      disableMenu={disableMenu}
      showStats={showStats}
      showHandle={placing ? false : undefined}
      showMenu={interacting ? false : undefined}
      onSelectMenu={handleOnSelectMenu}
      // Drag-related props below, may not be used (yet) depending on the type
      scale={zoom}
      onStart={handleOnStart}
      onStop={handleOnStop}
    />
  );

  const card = (
    <ErrorBoundaryResettable FallbackComponent={ErrorCard}>
      <ConfiguredStepCard
        {...props}
        type={type}
        step={data}
        showHandle={placing ? false : undefined}
        showMenu={interacting ? false : undefined}
        onSelectMenu={handleOnSelectMenu}
        // Drag-related props below, may not be used (yet) depending on the type
        scale={zoom}
        onStart={handleOnStart}
        onStop={handleOnStop}
      />
    </ErrorBoundaryResettable>
  );

  if (!isStepSlotNode(type)) {
    return card;
  }

  return (
    <StepSlot>
      <div>{card}</div>
    </StepSlot>
  );
};
